import React, { useCallback, useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { CheckCircle2, Gamepad2, Info, Layers, Link2, Power, RotateCcw, Timer } from '../ui/icons';
import Card from '../ui/Card';
import Button from '../ui/Button';
import SearchableSelect from '../ui/SearchableSelect';
import Colors, { withAlpha } from '../../constants/Colors';
import { appSettingsApi, gamesApi, Game, PreviewUrlSetting } from '../../services/api';

type SportScope = 'all' | 'basketball' | 'football';

// Stored in {env}-settings under setting-key "preview-url-setting" (see
// HomeStream-Admin-Backend routers/app_settings.py). HS-preview-url still signs links
// for a fixed 4h until it is changed to read this setting.
type PreviewUrlTimerSettingsValue = PreviewUrlSetting;

const DEFAULT_MINUTES = 240; // matches the Lambda's current hard-coded 4 hours
const MIN_MINUTES = 5;
const MAX_MINUTES = 720;
const PRESETS = [15, 30, 60, 120, 240, 480];

const DEFAULT_VALUE: PreviewUrlTimerSettingsValue = {
  enabled: true,
  expires_in_minutes: DEFAULT_MINUTES,
  sport: 'all',
  game_scope: 'all',
  game_id: null,
};

const SPORT_OPTIONS: { value: SportScope; label: string; icon: React.ReactNode }[] = [
  { value: 'all', label: 'All sports', icon: <Layers size={18} /> },
  { value: 'basketball', label: 'Basketball', icon: <span style={{ fontSize: '17px', lineHeight: 1 }}>🏀</span> },
  { value: 'football', label: 'Football', icon: <span style={{ fontSize: '17px', lineHeight: 1 }}>🏈</span> },
];

function formatMinutes(total: number): string {
  const hours = Math.floor(total / 60);
  const minutes = total % 60;
  if (hours && minutes) return `${hours}h ${minutes}m`;
  if (hours) return `${hours}h`;
  return `${minutes}m`;
}

function OptionTile({
  active,
  disabled,
  onClick,
  icon,
  label,
}: {
  active: boolean;
  disabled: boolean;
  onClick: () => void;
  icon: React.ReactNode;
  label: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-pressed={active}
      style={{
        flex: '1 1 0',
        minWidth: 0,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '6px',
        padding: '12px 8px',
        borderRadius: '12px',
        border: `1.5px solid ${active ? Colors.primary : withAlpha(Colors.border, 0.18)}`,
        background: active ? withAlpha(Colors.primary, 0.08) : '#FFFFFF',
        color: active ? Colors.primary : Colors.textLight,
        fontSize: '12.5px',
        fontWeight: 700,
        cursor: disabled ? 'not-allowed' : 'pointer',
        transition: 'all 0.15s',
      }}
    >
      {/* Fixed-height icon slot so emoji and SVG icons keep labels on one baseline */}
      <span style={{ height: '20px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>{icon}</span>
      {label}
    </button>
  );
}

function SectionLabel({ step, title, hint }: { step: number; title: string; hint: string }) {
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', marginBottom: '10px' }}>
      <span
        style={{
          flex: 'none',
          width: '22px',
          height: '22px',
          borderRadius: '999px',
          background: Colors.primary,
          color: '#FFFFFF',
          fontSize: '11px',
          fontWeight: 700,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {step}
      </span>
      <div>
        <div style={{ fontSize: '13px', fontWeight: 700, color: Colors.text }}>{title}</div>
        <div style={{ fontSize: '11.5px', color: Colors.textLight, marginTop: '1px' }}>{hint}</div>
      </div>
    </div>
  );
}

export default function PreviewUrlTimerSettings() {
  const [value, setValue] = useState<PreviewUrlTimerSettingsValue>(DEFAULT_VALUE);
  const [savedValue, setSavedValue] = useState<PreviewUrlTimerSettingsValue>(DEFAULT_VALUE);
  const [games, setGames] = useState<Game[]>([]);
  const [gamesStatus, setGamesStatus] = useState<'idle' | 'loading' | 'loaded' | 'error'>('idle');
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [savedMeta, setSavedMeta] = useState<{ exists: boolean; updatedAt?: string | null; table?: string }>({ exists: false });

  const update = (patch: Partial<PreviewUrlTimerSettingsValue>) => setValue((current) => ({ ...current, ...patch }));
  const off = !value.enabled;
  const dirty = JSON.stringify(value) !== JSON.stringify(savedValue);

  // Loads once; a failed load is retried the next time the picker opens.
  const loadGames = useCallback(async () => {
    if (gamesStatus === 'loading' || gamesStatus === 'loaded') return;
    setGamesStatus('loading');
    const response = await gamesApi.getScheduledTodayAndUpcomingGames();
    if (response.success && response.data) {
      setGames((response.data.games || []).filter((g) => String(g.status || '').toLowerCase() === 'scheduled'));
      setGamesStatus('loaded');
    } else {
      setGamesStatus('error');
      toast.error(response.error || 'Failed to load scheduled games');
    }
  }, [gamesStatus]);

  const loadSetting = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    const response = await appSettingsApi.getPreviewUrlSetting();
    if (response.success && response.data) {
      setValue(response.data.setting);
      setSavedValue(response.data.setting);
      setSavedMeta({ exists: response.data.exists, updatedAt: response.data.updated_at, table: response.data.table_name });
    } else {
      setLoadError(response.error || 'Failed to load preview URL setting');
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    loadSetting();
  }, [loadSetting]);

  // Start loading as soon as "Specific game" is chosen (or a saved per-game setting needs the
  // game's name) rather than waiting for the picker to open - the request can take several seconds.
  useEffect(() => {
    if (value.game_scope === 'specific' && gamesStatus === 'idle') loadGames();
  }, [value.game_scope, gamesStatus, loadGames]);

  // Game picker only lists games of the chosen sport.
  const gameOptions = useMemo(
    () =>
      games
        .filter((g) => value.sport === 'all' || String(g.game_type || '').toLowerCase() === value.sport)
        .map((g) => ({
          value: g.game_id,
          label: `${g.home_team} vs ${g.away_team} (${g.date})`,
          searchText: `${g.home_team} ${g.away_team} ${g.game_name || ''} ${g.date}`,
        })),
    [games, value.sport]
  );
  const selectedGame = games.find((g) => g.game_id === value.game_id);

  const setSport = (sport: SportScope) => {
    // Drop a selected game that no longer matches the sport.
    const keepGame = !selectedGame || sport === 'all' || String(selectedGame.game_type || '').toLowerCase() === sport;
    update({ sport, game_id: keepGame ? value.game_id : null });
  };

  const setMinutes = (minutes: number) => {
    if (Number.isNaN(minutes)) return;
    update({ expires_in_minutes: Math.min(MAX_MINUTES, Math.max(MIN_MINUTES, Math.round(minutes))) });
  };

  const scopeText = (() => {
    const sport = value.sport === 'all' ? '' : `${value.sport} `;
    if (value.game_scope === 'specific') {
      return selectedGame ? `${selectedGame.home_team} vs ${selectedGame.away_team}` : 'the selected game';
    }
    return `all ${sport}games`;
  })();

  const handleSave = async () => {
    if (value.enabled && value.game_scope === 'specific' && !value.game_id) {
      toast.error('Pick a game, or apply the timer to all games.');
      return;
    }
    setSaving(true);
    const response = await appSettingsApi.savePreviewUrlSetting(value);
    setSaving(false);
    if (response.success && response.data) {
      setValue(response.data.setting);
      setSavedValue(response.data.setting);
      setSavedMeta({ exists: true, updatedAt: response.data.updated_at, table: response.data.table_name });
      toast.success('Preview timer saved');
    } else {
      toast.error(response.error || 'Failed to save preview timer');
    }
  };

  return (
    <Card
      title="Preview Timer"
      description="How long a signed preview stream link stays valid before it expires."
      headerAction={
        <button
          type="button"
          role="switch"
          aria-checked={value.enabled}
          onClick={() => update({ enabled: !value.enabled })}
          disabled={loading || !!loadError}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '10px',
            padding: '6px 12px 6px 6px',
            borderRadius: '999px',
            border: `1px solid ${value.enabled ? withAlpha(Colors.success, 0.4) : withAlpha(Colors.border, 0.2)}`,
            background: value.enabled ? withAlpha(Colors.success, 0.1) : '#F5F7FC',
            cursor: 'pointer',
          }}
        >
          <span
            style={{
              position: 'relative',
              width: '40px',
              height: '22px',
              borderRadius: '999px',
              background: value.enabled ? Colors.success : '#C7CFE2',
              transition: 'background 0.2s',
              flex: 'none',
            }}
          >
            <span
              style={{
                position: 'absolute',
                top: '3px',
                left: value.enabled ? '21px' : '3px',
                width: '16px',
                height: '16px',
                borderRadius: '999px',
                background: '#FFFFFF',
                boxShadow: '0 1px 3px rgba(0,0,0,0.25)',
                transition: 'left 0.2s',
              }}
            />
          </span>
          <span style={{ fontSize: '12.5px', fontWeight: 700, color: value.enabled ? '#15803d' : Colors.textLight }}>
            {value.enabled ? 'Enabled' : 'Disabled'}
          </span>
        </button>
      }
    >
      {loadError && (
        <div className="alert alert-danger" style={{ marginBottom: '14px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px' }}>
          <span>{loadError}</span>
          <Button variant="outline" className="btn-small" onClick={loadSetting}>Retry</Button>
        </div>
      )}

      {/* Live summary of what the settings mean */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '14px',
          padding: '14px 16px',
          borderRadius: '14px',
          marginBottom: '18px',
          background: off
            ? '#F5F7FC'
            : `linear-gradient(135deg, ${withAlpha(Colors.primary, 0.1)}, ${withAlpha(Colors.secondary, 0.04)})`,
          border: `1px solid ${off ? withAlpha(Colors.border, 0.14) : withAlpha(Colors.primary, 0.18)}`,
        }}
      >
        <span
          style={{
            flex: 'none',
            width: '44px',
            height: '44px',
            borderRadius: '12px',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: off ? '#E3E8F3' : Colors.primary,
            color: off ? Colors.textLight : '#FFFFFF',
          }}
        >
          {off ? <Power size={20} /> : <Link2 size={20} />}
        </span>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: '22px', fontWeight: 800, color: off ? Colors.textLight : Colors.primary, lineHeight: 1.1 }}>
            {off ? 'Timer off' : formatMinutes(value.expires_in_minutes)}
          </div>
          <div style={{ fontSize: '12.5px', color: Colors.textLight, marginTop: '3px' }}>
            {off
              ? 'Preview links keep the default expiry (4h).'
              : <>Preview links for <strong style={{ color: Colors.text }}>{scopeText}</strong> expire {formatMinutes(value.expires_in_minutes)} after they are issued.</>}
          </div>
        </div>
      </div>

      <fieldset disabled={off || loading || !!loadError} style={{ border: 0, padding: 0, margin: 0, opacity: off || loading ? 0.5 : 1, transition: 'opacity 0.2s' }}>
        {/* 1. Timer */}
        <div style={{ marginBottom: '20px' }}>
          <SectionLabel step={1} title="Link expiry" hint={`Between ${formatMinutes(MIN_MINUTES)} and ${formatMinutes(MAX_MINUTES)}.`} />
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '12px' }}>
            {PRESETS.map((minutes) => {
              const active = value.expires_in_minutes === minutes;
              return (
                <button
                  key={minutes}
                  type="button"
                  onClick={() => setMinutes(minutes)}
                  style={{
                    padding: '6px 12px',
                    borderRadius: '999px',
                    border: `1px solid ${active ? Colors.primary : withAlpha(Colors.border, 0.2)}`,
                    background: active ? Colors.primary : '#FFFFFF',
                    color: active ? '#FFFFFF' : Colors.text,
                    fontSize: '12px',
                    fontWeight: 700,
                    cursor: off ? 'not-allowed' : 'pointer',
                  }}
                >
                  {formatMinutes(minutes)}
                  {minutes === DEFAULT_MINUTES && <span style={{ opacity: 0.7, fontWeight: 600 }}> · default</span>}
                </button>
              );
            })}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', maxWidth: '420px' }}>
            <Timer size={16} color={Colors.textLight} style={{ flex: 'none' }} />
            <input
              type="range"
              min={MIN_MINUTES}
              max={MAX_MINUTES}
              step={5}
              value={value.expires_in_minutes}
              onChange={(e) => setMinutes(Number(e.target.value))}
              aria-label="Link expiry in minutes"
              style={{ flex: 1, accentColor: Colors.primary }}
            />
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px', flex: 'none' }}>
              <input
                type="number"
                className="form-input"
                min={MIN_MINUTES}
                max={MAX_MINUTES}
                value={value.expires_in_minutes}
                onChange={(e) => setMinutes(Number(e.target.value))}
                aria-label="Link expiry in minutes"
                style={{ width: '84px', textAlign: 'right' }}
              />
              <span style={{ fontSize: '12px', color: Colors.textLight }}>min</span>
            </div>
          </div>
        </div>

        {/* 2. Sport */}
        <div style={{ marginBottom: '20px' }}>
          <SectionLabel step={2} title="Sport" hint="Apply to every sport, or just one." />
          <div style={{ display: 'flex', gap: '8px', maxWidth: '420px' }}>
            {SPORT_OPTIONS.map((option) => (
              <OptionTile
                key={option.value}
                active={value.sport === option.value}
                disabled={off}
                onClick={() => setSport(option.value)}
                icon={option.icon}
                label={option.label}
              />
            ))}
          </div>
        </div>

        {/* 3. Games */}
        <div>
          <SectionLabel step={3} title="Games" hint="Apply to every game, or override one game." />
          <div style={{ display: 'flex', gap: '8px', maxWidth: '420px' }}>
            <OptionTile
              active={value.game_scope === 'all'}
              disabled={off}
              onClick={() => update({ game_scope: 'all', game_id: null })}
              icon={<Layers size={18} />}
              label="All games"
            />
            <OptionTile
              active={value.game_scope === 'specific'}
              disabled={off}
              onClick={() => update({ game_scope: 'specific' })}
              icon={<Gamepad2 size={18} />}
              label="Specific game"
            />
          </div>
          {value.game_scope === 'specific' && (
            <div style={{ marginTop: '10px', maxWidth: '420px' }}>
              <SearchableSelect
                value={value.game_id || ''}
                onChange={(gameId) => update({ game_id: gameId || null })}
                onOpen={loadGames}
                options={gameOptions}
                placeholder={value.sport === 'all' ? 'Search scheduled games...' : `Search scheduled ${value.sport} games...`}
                emptyMessage={
                  gamesStatus === 'loading' || gamesStatus === 'idle'
                    ? 'Loading scheduled games...'
                    : gamesStatus === 'error'
                      ? 'Could not load games - close and reopen to retry'
                      : 'No scheduled games found'
                }
                disabled={off}
              />
            </div>
          )}
        </div>
      </fieldset>

      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '12px',
          flexWrap: 'wrap',
          marginTop: '20px',
          paddingTop: '14px',
          borderTop: `1px solid ${withAlpha(Colors.border, 0.14)}`,
        }}
      >
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: dirty ? '#B45309' : Colors.textLight }}>
          {dirty ? <Info size={14} /> : <CheckCircle2 size={14} />}
          {loading
            ? 'Loading saved setting...'
            : dirty
              ? 'Unsaved changes'
              : savedMeta.exists
                ? `Saved${savedMeta.updatedAt ? ` ${new Date(savedMeta.updatedAt).toLocaleString()}` : ''}${savedMeta.table ? ` · ${savedMeta.table}` : ''}`
                : 'Not saved yet - showing defaults'}
        </span>
        <div style={{ display: 'flex', gap: '8px' }}>
          <Button variant="outline" className="btn-small" onClick={() => setValue(savedValue)} disabled={!dirty || saving}>
            <RotateCcw size={14} />
            Reset
          </Button>
          <Button
            variant="primary"
            className="btn-small"
            onClick={handleSave}
            loading={saving}
            disabled={loading || !!loadError || (!dirty && savedMeta.exists)}
          >
            Save
          </Button>
        </div>
      </div>
    </Card>
  );
}
